/**
 * A catalogue line's delivery choice on the wire (`DeliverySelectionSchema`,
 * order to invoice doc 104 §9.1), shared by the price preview and the basket
 * line so both send the same thing for the same choice.
 *
 * Only what belongs to the mode is sent, and a pair is sent only whole: the
 * server refuses an agreed price without its reason, or a weight without its
 * parcels, and the preview must keep pricing while the CSR is still typing.
 */

import type { components } from "./api-schema.generated";
import type { CatalogueDeliverySelection } from "./catalogue-types";

export type DeliverySelectionBody =
  components["schemas"]["DeliverySelectionSchema"];

/** The delivery intents the schema accepts; a mailing travels as none. */
const WIRE_MODES = new Set(["ship", "multi", "collect", "hold", "tbc"]);

export function deliverySelectionBody(
  delivery: CatalogueDeliverySelection,
): DeliverySelectionBody {
  const mode = WIRE_MODES.has(delivery.mode)
    ? (delivery.mode as NonNullable<DeliverySelectionBody["mode"]>)
    : null;
  const splits = mode === "multi";
  // A split line's service is chosen per address at checkout (doc 106 §3.4):
  // the item page sends only where each share goes.
  const ships = mode === "ship";
  const collects = mode === "collect";
  const reason = delivery.agreedDeliveryReason?.trim() || null;
  const hasAgreedPrice =
    ships && delivery.agreedDeliveryPricePence != null && reason != null;
  const hasPackages =
    ships && delivery.weightKg != null && delivery.parcels != null;
  return {
    mode,
    delivery_address_id: ships ? delivery.deliveryAddressId : null,
    // Only a split sends rows; every other choice reads as before on the wire.
    ...(splits
      ? {
          delivery_lines: (delivery.deliveryLines ?? [])
            .filter((row) => row.deliveryAddressId != null && row.quantity > 0)
            .map((row) => ({
              delivery_address_id: row.deliveryAddressId as string,
              quantity: row.quantity,
            })),
        }
      : {}),
    service_level: ships && !hasAgreedPrice ? delivery.serviceLevel : null,
    courier_supplier_id:
      ships && !hasAgreedPrice ? delivery.courierSupplierId : null,
    agreed_delivery_price_pence: hasAgreedPrice
      ? delivery.agreedDeliveryPricePence
      : null,
    agreed_delivery_reason: hasAgreedPrice ? reason : null,
    weight_kg: hasPackages ? delivery.weightKg : null,
    parcels: hasPackages ? delivery.parcels : null,
    collection_point_address_id: collects
      ? delivery.collectionPointAddressId
      : null,
    recipient_name: collects ? delivery.recipientName?.trim() || null : null,
    recipient_phone: collects ? delivery.recipientPhone?.trim() || null : null,
  };
}
